import React, { useCallback, useEffect, useRef, useState, Fragment } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ChevronRightIcon, HomeIcon, LoaderIcon, AlertCircleIcon, UsersIcon } from 'lucide-react';
import { degreeCategories, degreeInterestOptions, focusOptions, getSpecializationsForFocus } from '../data/degrees';
import { searchSchools, MatchedSchool } from '../services/matchingApi';
import { MatchedSchoolCard, DidYouKnowBanner } from '../components/MatchedSchoolCard';
import { TrustSignals } from '../components/TrustSignals';
import { useFormState } from '../hooks/useFormState';
import { ProgramContentSection } from '../components/ProgramContentSection';
import { HomeInlineLeadCapture } from '../components/HomeInlineLeadCapture';
import { usePageMeta } from '../hooks/usePageMeta';
import { readMatchParams, resolveMatchPrefill, buildMatchHeadline, readCopyOverrides } from '../utils/matchParams';
export function DegreeListingPage() {
  const {
    category
  } = useParams<{
    category: string;
  }>();
  const navigate = useNavigate();
  const categoryData = degreeCategories.find(c => c.id === category);
  // Anchor and campaign links carry the visitor's context
  // (?degreeInterest=&areaStudy=&mProgram=), which turns this page into a
  // program-specific landing page: it drives the widget, the headline, and the
  // school listings so all three agree. Resolved once — the URL doesn't change
  // while the page is open.
  const [prefill] = useState(() => resolveMatchPrefill(readMatchParams()));
  const [copy] = useState(() => readCopyOverrides());
  const headline = buildMatchHeadline(prefill, categoryData?.name || 'Degree', copy.headline);
  const subhead = copy.subhead || (prefill.programLabel ? `Compare accredited online ${prefill.programLabel.toLowerCase()} programs and find the right fit for your career goals.` : `${categoryData?.description || ''} Compare top accredited programs and find the right fit for your career goals.`);
  usePageMeta(`${headline} | OnlineCollegeGuide`, `Compare accredited online ${(prefill.programLabel || categoryData?.name || 'degree').toLowerCase()} programs. Get matched by career goals, schedule, and budget.`, {
    ogImage: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=1200&h=630&fit=crop&q=80',
    ogType: 'website'
  });
  const {
    formData,
    errors,
    updateFormData,
    validateHeroForm
  } = useFormState({
    degreeInterest: prefill.degreeInterest,
    areaOfStudy: prefill.areaOfStudy || categoryData?.focusValue || '',
    specialization: prefill.specialization
  });
  const filteredSpecializations = formData.areaOfStudy ? getSpecializationsForFocus(formData.areaOfStudy) : [];
  // API state
  const [matchedSchools, setMatchedSchools] = useState<MatchedSchool[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const handleAreaOfStudyChange = (value: string) => {
    updateFormData('areaOfStudy', value);
    updateFormData('specialization', '');
  };
  const generalSpec = categoryData?.focusValue ? getSpecializationsForFocus(categoryData.focusValue).find(s => s.type.toLowerCase().startsWith('general') || s.type.toLowerCase().startsWith('all')) : undefined;
  // Fetch results from API
  const fetchResults = useCallback(async (degreeInterest: string, areaStudy: string, mProgram: string) => {
    setIsLoading(true);
    setApiError(null);
    setHasSearched(true);
    const result = await searchSchools({
      degreeInterest: degreeInterest || undefined,
      areaStudy: areaStudy || undefined,
      mProgram: mProgram || undefined
    });
    if (result.success) {
      setMatchedSchools(result.schools);
    } else {
      setApiError('Unable to load school matches. Please try again.');
      setMatchedSchools([]);
    }
    setIsLoading(false);
  }, []);
  // Auto-fetch on page load. URL context wins, so the listings match the
  // program named in the headline rather than the whole category.
  useEffect(() => {
    const area = prefill.areaOfStudy || categoryData?.focusValue;
    if (!area) return;
    fetchResults(prefill.degreeInterest, area, prefill.specialization || generalSpec?.value || '');
  }, [categoryData?.focusValue, fetchResults, generalSpec?.value, prefill]);
  // On mobile the search card sits above the listings, so a new search would
  // otherwise leave the visitor looking at the form they just submitted.
  const resultsRef = useRef<HTMLDivElement>(null);
  const pendingScrollRef = useRef(false);
  const handleSidebarSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateHeroForm()) {
      pendingScrollRef.current = true;
      fetchResults(formData.degreeInterest, formData.areaOfStudy, formData.specialization);
    }
  };
  useEffect(() => {
    if (isLoading || !pendingScrollRef.current) return;
    const target = resultsRef.current;
    pendingScrollRef.current = false;
    if (!target || window.innerWidth >= 1024) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const raf = requestAnimationFrame(() => {
      target.scrollIntoView({
        behavior: reduceMotion ? 'auto' : 'smooth',
        block: 'start'
      });
    });
    return () => cancelAnimationFrame(raf);
  }, [isLoading]);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [category]);
  if (!categoryData) {
    return <div className="min-h-[60vh] flex flex-col items-center justify-center bg-light px-4" data-id="element-1263" data-mp="172pjqw95huqk">
        <h1 className="text-3xl font-bold text-navy mb-4" data-id="element-1264" data-mp="123v23xmx6c81">
          Category Not Found
        </h1>
        <p className="text-gray-600 mb-8" data-id="element-1265" data-mp="x5bg5x8bt0fo">
          We couldn't find the degree category you're looking for.
        </p>
        <Link to="/" className="text-white bg-navy px-6 py-2 rounded-md hover:bg-navy-light transition-colors" data-id="element-1266" data-mp="t2erppw3ua3t">
          Return Home
        </Link>
      </div>;
  }
  return <main className="flex-grow bg-light pb-16" data-id="element-1267" data-mp="egr9ia9gpfnl">
      {/* Page Header */}
      <div className="hero-bg text-white py-12" data-id="element-1268" data-mp="eiu83iookh3x">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1269" data-mp="iil1zv9i7dco">
          {/* Breadcrumbs */}
          <nav className="flex text-sm text-gray-300 mb-6" aria-label="Breadcrumb" data-id="element-1270" data-mp="4jd2f19fi37y">
            <ol className="inline-flex items-center space-x-1 md:space-x-3" data-id="element-1271" data-mp="9hentsubueml">
              <li className="inline-flex items-center" data-id="element-1272" data-mp="9jhhcmuoupw2">
                <Link to="/" className="inline-flex items-center hover:text-white transition-colors" data-id="element-1273" data-mp="x8jyltleugq1">
                  <HomeIcon className="w-4 h-4 mr-2" data-id="element-1274" data-mp="125sji1xy0yuz" />
                  Home
                </Link>
              </li>
              <li data-id="element-1275" data-mp="nh5vzcy5x0so">
                <div className="flex items-center" data-id="element-1276" data-mp="sgirem33ng6v">
                  <ChevronRightIcon className="w-4 h-4 mx-1" data-id="element-1277" data-mp="sf7mgixx42e6" />
                  <span className="ml-1 md:ml-2" data-id="element-1278" data-mp="1g6czyrgdyilb">Degrees</span>
                </div>
              </li>
              <li aria-current="page" data-id="element-1279" data-mp="1kxbky4cbk4uw">
                <div className="flex items-center" data-id="element-1280" data-mp="1kyebuc9n67at">
                  <ChevronRightIcon className="w-4 h-4 mx-1" data-id="element-1281" data-mp="35myw33nh2z9g" />
                  <span className="ml-1 md:ml-2 text-white font-medium" data-id="element-1282" data-mp="35l48tioqav27">
                    {prefill.programLabel || categoryData.name}
                  </span>
                </div>
              </li>
            </ol>
          </nav>

          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-white" data-id="element-1283" data-mp="3ae76o30jgqo7">
            {headline}
          </h1>
          <p className="text-lg text-gray-300 max-w-3xl" data-id="element-1284" data-mp="2mnl06g66negb">{subhead}</p>
        </div>
      </div>

      <TrustSignals data-id="element-1285" data-mp="2hq1krcxr0vms" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-1286" data-mp="2hqkz6jo6x1w2">
        <div className="flex flex-col lg:flex-row gap-8" data-id="element-1287" data-mp="2vpazs9a7xjp6">
          {/* Main Content - Listings */}
          <div ref={resultsRef} id="school-matches" className="lg:w-3/4 order-2 lg:order-1 scroll-mt-20" data-id="element-1288" data-mp="2qpolrxmkfzfb">
            {/* Loading State */}
            {isLoading && <div className="flex flex-col items-center justify-center py-16" data-id="element-1289" data-mp="b0wmdpfhmtkf">
                <LoaderIcon className="w-8 h-8 text-navy animate-spin mb-4" data-id="element-1290" data-mp="5zh3o40s8z57" />
                <p className="text-gray-600 font-medium" data-id="element-1291" data-mp="q2wkruwp79lz">
                  Finding your best school matches...
                </p>
              </div>}

            {/* Error State */}
            {!isLoading && apiError && <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex items-start gap-3" data-id="element-1292" data-mp="z0ypakfn4ke0">
                <AlertCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-1293" data-mp="13ut4bakawb6r" />
                <div data-id="element-1294" data-mp="18rjmc3fo1gut">
                  <p className="text-red-800 font-medium" data-id="element-1295" data-mp="18s30ra63xn43">{apiError}</p>
                  <p className="text-red-600 text-sm mt-1" data-id="element-1296" data-mp="1dq4mk2ux40j9">
                    Try adjusting your search criteria or check back later.
                  </p>
                </div>
              </div>}

            {/* Results */}
            {!isLoading && !apiError && matchedSchools.length > 0 && <div className="space-y-4" data-id="element-1297" data-mp="3kinkyryddzpw">
                {matchedSchools.map((school, idx) => <Fragment key={(school as Record<string, unknown>).schoolid as string || idx} data-id="element-1298" data-mp="3oj7dtjbwuh68" data-mpi={idx}>
                    <MatchedSchoolCard school={school} index={idx} data-id="element-1299" data-mp="3gzogs95u0nen" />
                    {idx === 0 && matchedSchools.length > 1 && <DidYouKnowBanner data-id="element-1300" data-mp="3c8pzp7d0rlac" />}
                  </Fragment>)}
              </div>}

            {/* Empty State */}
            {!isLoading && !apiError && hasSearched && matchedSchools.length === 0 && <div className="bg-white rounded-xl border border-gray-200 p-12 text-center" data-id="element-1301" data-mp="33935p6mmdhrb">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4" data-id="element-1302" data-mp="3t6aqkjdsp13">
                    <AlertCircleIcon className="w-8 h-8 text-gray-400" data-id="element-1303" data-mp="3uzheiofs03w4" />
                  </div>
                  <h3 className="text-xl font-bold text-navy mb-2" data-id="element-1304" data-mp="3qxcl9qt8p9a0">
                    No matches found
                  </h3>
                  <p className="text-gray-500 max-w-md mx-auto" data-id="element-1305" data-mp="3lyj7dxa1jg9b">
                    Try adjusting your degree level or program selection in the
                    sidebar to find more results.
                  </p>
                </div>}
          </div>

          {/* Sidebar - Form */}
          <div className="lg:w-1/4 order-1 lg:order-2" data-id="element-1306" data-mp="pq3loesf4pcf">
            <div className="sticky top-24" data-id="element-1307" data-mp="kpxut0tz36ve">
              <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden mb-6" data-id="element-1308" data-mp="kr8v2qo7lwhr">
                <div className="bg-navy px-6 py-4" data-id="element-1309" data-mp="yix6ssujqiy7">
                  <h3 className="text-white font-bold text-center text-sm tracking-widest uppercase" data-id="element-1310" data-mp="tqvx9eorj6bv">
                    Get Matched Fast
                  </h3>
                  <p className="text-gray-300 text-xs text-center mt-1" data-id="element-1311" data-mp="18dp6q3blphw5">
                    Find the right program for you
                  </p>
                </div>

                <form onSubmit={handleSidebarSubmit} className="p-5 space-y-4" data-id="element-1312" data-mp="1hf6u86rc8luv">
                  {/* Degree Level */}
                  <div data-id="element-1313" data-mp="1df4pfoqb3cbd">
                    <select id="sidebar-degreeInterest" value={formData.degreeInterest} onChange={e => updateFormData('degreeInterest', e.target.value)} className={`block w-full px-3 py-2.5 text-sm border rounded-md bg-white appearance-none cursor-pointer ${errors.degreeInterest ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-navy focus:border-navy'}`} data-id="element-1314" data-mp="1dcsazljrbk32">
                      <option value="" data-id="element-1315" data-mp="395h9ydf4nxeq">Select Degree Level</option>
                      {degreeInterestOptions.map(opt => <option key={opt.value} value={opt.value} data-id="element-1316" data-mp="2vaf5lupxig41">
                          {opt.type}
                        </option>)}
                    </select>
                    {errors.degreeInterest && <p className="mt-1 text-xs text-red-600" data-id="element-1317" data-mp="2lg6hdj7xl9bo">
                        {errors.degreeInterest}
                      </p>}
                  </div>

                  {/* Area of Study */}
                  <div data-id="element-1318" data-mp="2cfgksdaujeww">
                    <select id="sidebar-areaOfStudy" value={formData.areaOfStudy} onChange={e => handleAreaOfStudyChange(e.target.value)} className={`block w-full px-3 py-2.5 text-sm border rounded-md bg-white appearance-none cursor-pointer ${errors.areaOfStudy ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-navy focus:border-navy'}`} data-id="element-1319" data-mp="2hc7191qkcciu">
                      <option value="" data-id="element-1320" data-mp="d441umbmn15b">Select Area of Study</option>
                      {focusOptions.map(opt => <option key={opt.value} value={opt.value} data-id="element-1321" data-mp="96y4cj8c1smx">
                          {opt.type}
                        </option>)}
                    </select>
                    {errors.areaOfStudy && <p className="mt-1 text-xs text-red-600" data-id="element-1322" data-mp="3vdwixvqdvoc7">
                        {errors.areaOfStudy}
                      </p>}
                  </div>

                  {/* Specialization / Program */}
                  <div data-id="element-1323" data-mp="rxftyexvc842">
                    <select id="sidebar-specialization" value={formData.specialization} onChange={e => updateFormData('specialization', e.target.value)} disabled={!formData.areaOfStudy} className={`block w-full px-3 py-2.5 text-sm border rounded-md bg-white appearance-none cursor-pointer disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed ${errors.specialization ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-navy focus:border-navy'}`} data-id="element-1324" data-mp="n4d2cccnj8qi">
                      <option value="" data-id="element-1325" data-mp="1n46ovlz1q8nn">
                        {formData.areaOfStudy ? 'Select Program' : 'Select an area of study first'}
                      </option>
                      {filteredSpecializations.map(opt => <option key={opt.value} value={opt.value} data-id="element-1326" data-mp="1x35qwlpb361o">
                          {opt.type}
                        </option>)}
                    </select>
                    {errors.specialization && <p className="mt-1 text-xs text-red-600" data-id="element-1327" data-mp="26317j61mrt93">
                        {errors.specialization}
                      </p>}
                  </div>

                  <button type="submit" disabled={isLoading} className="w-full bg-cta hover:bg-cta-dark disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold py-4 px-4 rounded-lg text-sm tracking-wider uppercase transition-colors duration-200 shadow-lg hover:shadow-xl flex items-center justify-center gap-2" data-id="element-1328" data-mp="2ausvr9xbdmw2">
                    {isLoading && <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-1329" data-mp="3mi5bxup8h3md" />}
                    {isLoading ? 'Searching...' : 'Search Programs'}
                  </button>
                </form>
              </div>

              {/* Lead capture. The card above filters the listings; this one
                  submits the visitor to the partner APIs, carrying whatever
                  program context is currently selected. */}
              <div className="mb-6" data-id="element-1330" data-mp="33kue8ej92i91">
                <HomeInlineLeadCapture degreeInterest={formData.degreeInterest} areaStudy={formData.areaOfStudy || categoryData.focusValue} mProgram={formData.specialization || generalSpec?.value || ''} title="Get Matched Now" subtitle="Accredited schools, no obligation" data-id="element-1331" data-mp="33lx2sft3o8ls" />
              </div>

              {/* Undecided nudge — for visitors who can't fill the form yet */}
              <Link to="/degrees" className="group flex items-center gap-3 bg-light hover:bg-white border border-gray-200 rounded-lg px-4 py-3 mb-6 transition-colors duration-200" data-id="element-1332" data-mp="munjww6gh5in">
                <span className="flex-shrink-0 w-9 h-9 rounded-full bg-white group-hover:bg-light border border-gray-200 flex items-center justify-center text-navy transition-colors duration-200" data-id="element-1333" data-mp="zeu8ct4odi4z">
                  <UsersIcon className="w-4 h-4" aria-hidden="true" data-id="element-1334" data-mp="qe5txxbddfrk" />
                </span>
                <span className="min-w-0 flex-1" data-id="element-1335" data-mp="1e2ffqz8l23ip">
                  <span className="block text-sm font-bold text-navy leading-tight" data-id="element-1336" data-mp="1e51jenscryvn">
                    Not sure what to study?
                  </span>
                  <span className="block text-xs text-gray-600 mt-0.5 leading-tight" data-id="element-1337" data-mp="14buj3hcgz92f">
                    Explore top programs by career
                  </span>
                </span>
                <ChevronRightIcon className="w-4 h-4 flex-shrink-0 text-gray-400 group-hover:text-navy transition-colors duration-200" aria-hidden="true" data-id="element-1338" data-mp="1x19s61kkqrvp" />
              </Link>

              {/* Related Programs */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-id="element-1339" data-mp="2bn75tkb16lp0">
                <h4 className="font-bold text-navy mb-4 border-b border-gray-100 pb-2" data-id="element-1340" data-mp="26ovsgzlj1t21">
                  Programs in {categoryData.name}
                </h4>
                <ul className="space-y-2" data-id="element-1341" data-mp="21rwzwlhihu5y">
                  {getSpecializationsForFocus(formData.areaOfStudy || categoryData.focusValue).map(spec => <li key={spec.value} data-id="element-1342" data-mp="1spnsi4hjigws">
                      <button onClick={() => {
                    updateFormData('specialization', spec.value);
                    fetchResults(formData.degreeInterest, formData.areaOfStudy || categoryData.focusValue, spec.value);
                  }} className={`text-sm flex items-center w-full text-left ${formData.specialization === spec.value ? 'text-crimson font-semibold' : 'text-gray-600 hover:text-crimson'}`} data-id="element-1343" data-mp="1sqp5wfm13fho">
                        <ChevronRightIcon className="w-3 h-3 mr-1 flex-shrink-0" data-id="element-1344" data-mp="14zthlsgs72f6" />
                        {spec.type}
                      </button>
                    </li>)}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ProgramContentSection degreeInterest={formData.degreeInterest} areaOfStudy={formData.areaOfStudy || categoryData.focusValue} specialization={formData.specialization || generalSpec?.value || ''} data-id="element-1345" data-mp="3lkezg2xgky1s" />
    </main>;
}